import { FileText, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { HelpSearchResults as HelpSearchResultsType } from "@/types/HelpArticle";
import { Skeleton } from "@/components/ui/skeleton";

interface HelpSearchResultsProps {
  results: HelpSearchResultsType | undefined;
  isLoading: boolean;
  query: string;
}

const HelpSearchResults = ({ results, isLoading, query }: HelpSearchResultsProps) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Searching...</h2>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!results || results.totalResults === 0) {
    return (
      <div className="mb-8 text-center py-8 bg-gray-50 rounded-md">
        <HelpCircle className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-2">No results found</h2>
        <p className="text-gray-600">
          We couldn't find any help articles or FAQs matching "{query}".
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Try different keywords or browse our help categories below.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold mb-4">
        Search Results ({results.totalResults})
      </h2>

      {/* Articles */}
      {results.articles.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3 text-gray-700">Articles</h3>
          <div className="space-y-3">
            {results.articles.map((article) => (
              <div
                key={article.id}
                className="bg-white border border-gray-200 rounded-md p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/help/article/${article.slug}`)}
              >
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg mb-1">{article.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{article.summary}</p>
                    {article.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQs */}
      {results.faqs.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-3 text-gray-700">FAQs</h3>
          <div className="space-y-3">
            {results.faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white border border-gray-200 rounded-md p-4"
              >
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">{faq.question}</h4>
                    <p className="text-sm text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpSearchResults;
